import { AppThunkAction } from ".";
import { Reducer } from "redux";
import { Action } from "redux";
import { IssuedOrder, PohodaClient } from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage, type ApiError } from "../utils/apiError";

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface IssuedOrdersState {
  isLoading: boolean;
  error: string;
  issuedOrders: IssuedOrder[];
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

interface LoadIssuedOrdersAction {
  type: "LOAD_UNSATISFIED_ISSUED_ORDERS";
}

interface ReceiveIssuedOrdersAction {
  type: "RECEIVE_ISSUED_ORDERS";
  issuedOrders: IssuedOrder[];
}

interface CancelLoadIssuedOrdersAction {
  type: "CANCEL_LOAD_ISSUED_ORDERS";
  reason: string;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | LoadIssuedOrdersAction
  | ReceiveIssuedOrdersAction
  | CancelLoadIssuedOrdersAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

// Initialize the API client
const pohodaClient = new PohodaClient(new ApiConfig());

export const actionCreators = {
  requestUnsatisfiedIssuedOrders:
    (): AppThunkAction<KnownAction> => (dispatch, getState) => {
      pohodaClient
        .loadUnsatisifedIssuedOrders()
        .then((backendData) => {
          dispatch({
            type: "RECEIVE_ISSUED_ORDERS",
            issuedOrders: backendData,
          });
        })
        .catch((error: ApiError) =>
          dispatch({
            type: "CANCEL_LOAD_ISSUED_ORDERS",
            reason: getApiErrorMessage(error),
          })
        );

      dispatch({ type: "LOAD_UNSATISFIED_ISSUED_ORDERS" });
    },
  //updateProductCode: (event: ChangeEvent<HTMLInputElement>) => ({ type: 'UPDATE_PRODUCT_CODE', code: event.target.value } as UpdateProductCodeAction),
  //selectBatch: (batch: ProductBatch) => ({ type: 'SELECT_BATCH', batch: batch } as SelectBatchAction),
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

const unloadedState: IssuedOrdersState = {
  isLoading: false,
  error: "",
  issuedOrders: [],
};

export const reducer: Reducer<IssuedOrdersState> = (
  state: IssuedOrdersState | undefined,
  incomingAction: Action
): IssuedOrdersState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case "LOAD_UNSATISFIED_ISSUED_ORDERS":
      return {
        ...state,
        error: "",
        isLoading: true,
      };
    case "RECEIVE_ISSUED_ORDERS":
      return {
        ...state,
        error: "",
        isLoading: false,
        issuedOrders: action.issuedOrders,
      };
    case "CANCEL_LOAD_ISSUED_ORDERS":
      return {
        ...state,
        isLoading: false,
        error: action.reason,
      };
    default:
      return state;
  }
};
